/**
 * navConfig.js — Single source of truth for application navigation.
 *
 * Top-level sections (15 total):
 *   1. Dashboard
 *   2. Service Desk
 *   3. Problem & Change
 *   4. Assets (Inventory & Operations only)
 *   5. Hardware
 *   6. Software
 *   7. Intelligence & Configuration
 *   8. CMDB
 *   9. Knowledge & Services
 *  10. Business Operations (Vendors / Warehouse / Projects / Finance)
 *  11. Reports
 *  12. People & Access
 *  13. Configuration (business/catalog setup)
 *  14. System (platform administration)
 *  15. Help
 */

import React from 'react';
import { isHrDepartmentUser } from '../utils/roles';
import {
  // Service Desk
  ConfirmationNumber,
  Assignment,
  HelpCenter,
  BugReport,
  Psychology,
  ReportProblem,
  MenuBook,
  // Assets
  Inventory2,
  DevicesOther,
  Build,
  CloudUpload,
  Storefront,
  Description,
  // CMDB
  AccountTree,
  Storage,
  BusinessCenter,
  Hub,
  Insights,
  BarChart,
  // Access Management
  ManageAccounts,
  CorporateFare,
  AdminPanelSettings,
  Groups,
  PersonAdd,
  ContactPhone,
  // Administration
  Notifications,
  VerifiedUser,
  Assessment,
  Speed,
  Settings,
  Email,
  // Server Requests
  Computer,
  VpnKey,
  Pool,
  // Hardware Requests
  Devices,
  ListAlt,
  LocalShipping,
  Dashboard,
  ShoppingCart,
  // Software Asset Management
  Apps,
  CardMembership,
  // Help
  HelpOutline,
  Memory,
  // Additional
  CalendarMonth,
  SwapHoriz,
  Reply,
  Schedule,
  Logout,
  AssignmentReturn,
  Palette,
  FolderSpecial,
  Autorenew,
  Campaign,
  PhoneAndroid,
  Visibility,
  AttachMoney,
} from '@mui/icons-material';

// ── Role groups (mirror App.js) ───────────────────────────
export const ROLES = {
  ICT:         ['admin', 'ict_manager', 'dashboard_viewer'],
  STAFF:       ['admin', 'ict_manager', 'jr_engineer', 'sr_engineer'],
  ENG:         ['jr_engineer', 'sr_engineer'],
  MGMT:        ['admin', 'ict_manager', 'employee_manager', 'project_manager', 'delivery_manager'],
  USER_MGT:    ['admin', 'ict_manager'],
  PROB_MGT:    ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'],
  KM_WRITE:    ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'],
  KM_APPROVAL: ['admin', 'ict_manager', 'sr_engineer'],
  CMDB_MGT:    ['admin', 'ict_manager', 'sr_engineer'],
  CMDB_ALL:    ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'],
  HW_ALL:      ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee'],
  HW_ICT:      ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'],
  SW_ALL:      ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee'],
  SW_ICT:      ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'],
  ALL:         ['admin', 'ict_manager', 'jr_engineer', 'sr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee', 'dashboard_viewer'],
};

/**
 * NAV_GROUPS — master navigation structure (regrouped / consolidated).
 */
export const NAV_GROUPS = [

  // ═══════════════════════════════════════════════════════
  // 1. DASHBOARD  (top-level, no header)
  // ═══════════════════════════════════════════════════════
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <Speed sx={{ fontSize: 18 }} />,
    topLevel: true,
    children: [
      {
        id: 'home',
        label: 'Dashboard',
        path: '/home',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
        exact: true,
      },
      {
        id: 'my-asset-forms',
        label: 'My Asset Forms',
        path: '/my-asset-forms',
        icon: <Description sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 2. SERVICE DESK
  // ═══════════════════════════════════════════════════════
  {
    id: 'service-desk',
    label: 'Service Desk',
    icon: <ConfirmationNumber sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'tickets',
        label: 'Tickets',
        path: '/tickets',
        icon: <ConfirmationNumber sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'ticket-pool',
        label: 'Ticket Pool',
        path: '/ticket-pool',
        icon: <Pool sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'my-tasks',
        label: 'My Tasks',
        path: '/my-tasks',
        icon: <Assignment sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'server-requests',
        label: 'Server Requests',
        path: '/server-requests',
        icon: <Computer sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'vpn-requests',
        label: 'VPN Requests',
        path: '/vpn-requests',
        icon: <VpnKey sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'software-requests',
        label: 'Software Requests',
        path: '/software-requests',
        icon: <Apps sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'hardware-my-requests',
        label: 'Hardware Requests',
        path: '/hardware/requests',
        icon: <Devices sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ALL,
      },
      {
        id: 'service-renewals',
        label: 'Reminders',
        path: '/service-renewals',
        icon: <Autorenew sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'contract-analysis',
        label: 'Contract Analysis',
        path: '/contract-analysis',
        icon: <Description sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 3. PROBLEM & CHANGE
  // ═══════════════════════════════════════════════════════
  {
    id: 'problem-change',
    label: 'Problem & Change',
    icon: <BugReport sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'problem-dashboard',
        label: 'Problem Dashboard',
        path: '/problem-dashboard',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.PROB_MGT,
        exact: true,
      },
      {
        id: 'problems',
        label: 'Problems',
        path: '/problems',
        icon: <BugReport sx={{ fontSize: 18 }} />,
        roles: ROLES.PROB_MGT,
      },
      {
        id: 'known-errors',
        label: 'Known Error Database',
        path: '/known-errors',
        icon: <ReportProblem sx={{ fontSize: 18 }} />,
        roles: ROLES.PROB_MGT,
      },
      {
        id: 'maintenance-schedule',
        label: 'Maintenance Schedule',
        path: '/maintenance-schedule',
        icon: <CalendarMonth sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'maintenance-mgmt',
        label: 'Maintenance Management',
        path: '/maintenance-mgmt',
        icon: <Build sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
        children: [
          { id: 'maint-dashboard', label: 'Dashboard', path: '/maintenance-mgmt' },
          { id: 'maint-list', label: 'All Maintenance', path: '/maintenance-mgmt/list' },
          { id: 'maint-new', label: 'Plan Maintenance', path: '/maintenance-mgmt/new' },
          { id: 'maint-calendar', label: 'Calendar', path: '/maintenance-mgmt/calendar' },
        ],
      },
      {
        id: 'backups',
        label: 'Backups',
        path: '/backups',
        icon: <CloudUpload sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 4. ASSETS  (Inventory & Operations only)
  // ═══════════════════════════════════════════════════════
  {
    id: 'assets',
    label: 'Assets',
    icon: <Inventory2 sx={{ fontSize: 18 }} />,
    children: [
      // Inventory & Operations
      {
        id: 'asset-management-dashboard',
        label: 'Asset Dashboard',
        path: '/assets/management-dashboard',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
        exact: true,
      },
      {
        id: 'asset-inventory',
        label: 'Asset Inventory',
        path: '/assets',
        icon: <DevicesOther sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
        exact: true,
      },
      {
        id: 'operations-hub',
        label: 'Operations Hub',
        path: '/operations-hub',
        icon: <Hub sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'employee-portfolio',
        label: 'Employee Portfolio',
        path: '/employee-portfolio',
        icon: <Groups sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'reservation-center',
        label: 'Reservations',
        path: '/reservation-center',
        icon: <CalendarMonth sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'asset-return-on-behalf',
        label: 'Asset Return on Behalf',
        path: '/asset-return-on-behalf',
        icon: <AssignmentReturn sx={{ fontSize: 18 }} />,
        // Gated by HR-department membership (plus admin/ict_manager), not a role.
        deptGate: isHrDepartmentUser,
      },
      {
        id: 'damage-recovery',
        label: 'Damage & Recovery',
        path: '/damage-recovery',
        icon: <ReportProblem sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ALL,
      },
      {
        id: 'qr-scanner',
        label: 'QR Scanner',
        path: '/qr-scanner',
        icon: <Devices sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      // Hardware section moved to separate top-level category
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 5. HARDWARE
  // ═══════════════════════════════════════════════════════
  {
    id: 'hardware',
    label: 'Hardware',
    icon: <Devices sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'hardware-dashboard',
        label: 'Hardware Dashboard',
        path: '/hardware/dashboard',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ICT,
        exact: true,
      },
      {
        id: 'hardware-fulfilment',
        label: 'Fulfilment Queue',
        path: '/hardware/fulfilment',
        icon: <LocalShipping sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ICT,
      },
      {
        id: 'hardware-procurement',
        label: 'Procurement',
        path: '/hardware/procurement',
        icon: <ShoppingCart sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ICT,
      },
      {
        id: 'hardware-manager-approvals',
        label: 'Manager Approvals',
        path: '/hardware/manager-approvals',
        icon: <VerifiedUser sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager', 'employee_manager', 'project_manager', 'delivery_manager'],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 6. SOFTWARE
  // ═══════════════════════════════════════════════════════
  {
    id: 'software',
    label: 'Software',
    icon: <Apps sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'software-dashboard',
        label: 'Software Dashboard',
        path: '/software/dashboard',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ICT,
        exact: true,
      },
      {
        id: 'software-catalog',
        label: 'Software Catalog',
        path: '/software/catalog',
        icon: <Apps sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ALL,
      },
      {
        id: 'my-software-licenses',
        label: 'My Software',
        path: '/software/my-licenses',
        icon: <CardMembership sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ALL,
      },
      {
        id: 'software-licenses',
        label: 'Licenses',
        path: '/software/licenses',
        icon: <CardMembership sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ICT,
      },
      {
        id: 'sam-compliance',
        label: 'SAM & Compliance',
        path: '/software/sam',
        icon: <CardMembership sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ICT,
      },
      {
        id: 'sam-assignments',
        label: 'SAM Assignments',
        path: '/software/assignments',
        icon: <CardMembership sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ICT,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 7. INTELLIGENCE & CONFIGURATION
  // ═══════════════════════════════════════════════════════
  {
    id: 'intelligence-configuration',
    label: 'Intelligence & Configuration',
    icon: <Insights sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'asset-intelligence',
        label: 'Intelligence Dashboard',
        path: '/asset-intelligence',
        icon: <Insights sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'inventory-intelligence',
        label: 'Inventory Intelligence',
        path: '/inventory-intelligence',
        icon: <Insights sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'asset-replacement',
        label: 'Replacement Planner',
        path: '/asset-intelligence/replacement',
        icon: <Build sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'vendor-scorecards',
        label: 'Vendor Scorecards',
        path: '/asset-intelligence/vendors',
        icon: <Storefront sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'configuration-dashboard',
        label: 'Configuration Dashboard',
        path: '/configuration-dashboard',
        icon: <Settings sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'component-registry',
        label: 'Component Registry',
        path: '/components',
        icon: <Memory sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'configuration-baselines',
        label: 'Configuration Baselines',
        path: '/configuration-baselines',
        icon: <VerifiedUser sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 8. CMDB
  // ═══════════════════════════════════════════════════════
  {
    id: 'cmdb',
    label: 'CMDB',
    icon: <AccountTree sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'cmdb-dashboard',
        label: 'Dashboard',
        path: '/cmdb',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
        exact: true,
      },
      {
        id: 'configuration-items',
        label: 'Configuration Items',
        path: '/cmdb/list',
        icon: <Storage sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'business-services',
        label: 'Business Services',
        path: '/cmdb/services',
        icon: <BusinessCenter sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'relationships',
        label: 'Relationships',
        path: '/cmdb/relationships',
        icon: <Hub sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'cmdb-topology',
        label: 'Topology',
        path: '/cmdb/topology',
        icon: <Hub sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'cmdb-dependencies',
        label: 'Dependencies',
        path: '/cmdb/dependencies',
        icon: <AccountTree sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'impact-analysis',
        label: 'Impact Analysis',
        path: '/cmdb/impact-analysis',
        icon: <Insights sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'cmdb-governance',
        label: 'Governance',
        path: '/cmdb/governance',
        icon: <Insights sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'cmdb-sync',
        label: 'Synchronization',
        path: '/cmdb/sync',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_MGT,
      },
      {
        id: 'cmdb-operations',
        label: 'Operations Center',
        path: '/cmdb/operations',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_MGT,
      },
      {
        id: 'cmdb-admin',
        label: 'CMDB Administration',
        path: '/cmdb/admin',
        icon: <Settings sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_MGT,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 9. KNOWLEDGE & SERVICES
  // ═══════════════════════════════════════════════════════
  {
    id: 'knowledge-services',
    label: 'Knowledge & Services',
    icon: <MenuBook sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'knowledge-base',
        label: 'Knowledge Base',
        path: '/knowledge',
        icon: <MenuBook sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'doc-library',
        label: 'Document Library',
        path: '/repository/documents',
        icon: <Description sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'doc-dashboard',
        label: 'Repository Dashboard',
        path: '/repository/dashboard',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'service-portal-browse',
        label: 'Browse Services',
        path: '/service-portal',
        icon: <Apps sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
        exact: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 10. BUSINESS OPERATIONS  (Vendors + Warehouse + Projects + Finance)
  // ═══════════════════════════════════════════════════════
  {
    id: 'business-operations',
    label: 'Business Operations',
    icon: <BusinessCenter sx={{ fontSize: 18 }} />,
    children: [
      // Vendors — all sub-functions live as tabs inside the Vendor Hub (/vendors).
      // Kept only the two most-used entries in the sidebar; Onboarding,
      // Certifications, Communications, Lifecycle, Governance, Risk, SLA Breaches,
      // Comparison, Spend, Invoices and Job Settings are reachable via the tabs.
      {
        id: 'vendors',
        label: 'Vendors',
        path: '/vendors',
        icon: <Storefront sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'vendor-contracts',
        label: 'Contracts',
        path: '/vendors/contracts',
        icon: <Description sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      // Warehouse & Logistics
      {
        id: 'warehouse-dashboard',
        label: 'Warehouse Dashboard',
        path: '/warehouse',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
        exact: true,
      },
      {
        id: 'warehouse-list',
        label: 'Warehouses',
        path: '/warehouse/list',
        icon: <Inventory2 sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'stock-explorer',
        label: 'Stock Explorer',
        path: '/warehouse/stock',
        icon: <DevicesOther sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'goods-receipt',
        label: 'Goods Receipt',
        path: '/warehouse/goods-receipt',
        icon: <LocalShipping sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'warehouse-operations',
        label: 'Warehouse Operations',
        path: '/warehouse/operations',
        icon: <Build sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      // Projects
      {
        id: 'projects',
        label: 'Projects',
        path: '/projects',
        icon: <FolderSpecial sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager', 'project_manager', 'delivery_manager'],
      },
      // Finance (consolidated)
      {
        id: 'finance-dashboard',
        label: 'Finance Dashboard',
        path: '/finance',
        icon: <Dashboard sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
        exact: true,
      },
      {
        id: 'finance-budgets',
        label: 'Budgets',
        path: '/finance/budgets',
        icon: <Assessment sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'finance-service-cost',
        label: 'ICT Expenses',
        path: '/finance/service-cost-tracking',
        icon: <AttachMoney sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'asset-financial',
        label: 'Asset Financial Management',
        path: '/asset-intelligence/financial',
        icon: <Assessment sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
        exact: true,
      },
      {
        id: 'asset-budget',
        label: 'Budget Forecast',
        path: '/asset-intelligence/budget',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      // Note: Vendor Spend Analysis and Vendor Invoices now live as tabs inside
      // the Vendor Hub (/vendors/spend, /vendors/invoices).
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 11. REPORTS
  // ═══════════════════════════════════════════════════════
  {
    id: 'reports',
    label: 'Reports',
    icon: <BarChart sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'reports-analytics',
        label: 'Reports & Analytics',
        path: '/reports',
        icon: <Assessment sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'asset-reports',
        label: 'Asset Management Reports',
        path: '/assets/reports',
        icon: <Assessment sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'my-report',
        label: 'My Performance',
        path: '/my-report',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: [...ROLES.ENG, 'admin', 'ict_manager'],
      },
      {
        id: 'engineer-performance',
        label: 'Engineer Analytics',
        path: '/engineer-performance',
        icon: <Assessment sx={{ fontSize: 18 }} />,
        roles: [...ROLES.ENG, 'admin', 'ict_manager', 'dashboard_viewer'],
      },
      {
        id: 'hardware-reports',
        label: 'Hardware Reports',
        path: '/hardware/reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.HW_ICT,
      },
      {
        id: 'sam-reports',
        label: 'Software / SAM Reports',
        path: '/software/sam-reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.SW_ICT,
      },
      {
        id: 'cmdb-reports',
        label: 'CMDB Reports',
        path: '/cmdb/reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.CMDB_ALL,
      },
      {
        id: 'maintenance-reports',
        label: 'Maintenance Reports',
        path: '/maintenance-mgmt/reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'warehouse-reports',
        label: 'Warehouse Reports',
        path: '/warehouse/reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'location-reports',
        label: 'Location Reports',
        path: '/locations/reports',
        icon: <BarChart sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 12. PEOPLE & ACCESS
  // ═══════════════════════════════════════════════════════
  {
    id: 'people-access',
    label: 'People & Access',
    icon: <ManageAccounts sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'user-management',
        label: 'User Management',
        path: '/users',
        icon: <ManageAccounts sx={{ fontSize: 18 }} />,
        roles: ROLES.USER_MGT,
      },
      {
        id: 'departments',
        label: 'Departments',
        path: '/departments',
        icon: <CorporateFare sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'contacts',
        label: 'Contacts',
        path: '/contacts',
        icon: <ContactPhone sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'roles-permissions',
        label: 'Roles & Permissions',
        path: '/roles',
        icon: <AdminPanelSettings sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
      {
        id: 'employee-lifecycle',
        label: 'Employee Lifecycle',
        path: '/team-dashboard',
        icon: <Groups sx={{ fontSize: 18 }} />,
        roles: ROLES.MGMT,
      },
      {
        id: 'invite-users',
        label: 'Invite Users',
        path: '/invite',
        icon: <PersonAdd sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'welcome-kits',
        label: 'Welcome Kits',
        path: '/welcome-kits',
        icon: <Email sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'my-policy-acceptance',
        label: 'My Policy Acceptance',
        path: '/welcome-acceptance',
        icon: <Campaign sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 13. CONFIGURATION  (business / catalog setup)
  // ═══════════════════════════════════════════════════════
  {
    id: 'configuration',
    label: 'Configuration',
    icon: <Settings sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'ticket-categories',
        label: 'Ticket Categories',
        path: '/ticket-categories',
        icon: <ConfirmationNumber sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'vendor-service-types',
        label: 'Vendor Service Types',
        path: '/vendor-service-types',
        icon: <Build sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'asset-categories',
        label: 'Asset Categories',
        path: '/asset-categories',
        icon: <Inventory2 sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'oem-settings',
        label: 'OEM / Manufacturers',
        path: '/oem-settings',
        icon: <BusinessCenter sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'sla-settings',
        label: 'SLA Settings',
        path: '/sla-settings',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
      {
        id: 'enterprise-locations',
        label: 'Enterprise Locations',
        path: '/locations',
        icon: <Inventory2 sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'location-tree',
        label: 'Location Tree',
        path: '/locations/tree',
        icon: <AccountTree sx={{ fontSize: 18 }} />,
        roles: ROLES.STAFF,
      },
      {
        id: 'theme-settings',
        label: 'Appearance & Theme',
        path: '/theme-settings',
        icon: <Palette sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════
  // 14. SYSTEM  (platform administration)
  // ═══════════════════════════════════════════════════════
  {
    id: 'system',
    label: 'System',
    icon: <AdminPanelSettings sx={{ fontSize: 18 }} />,
    children: [
      {
        id: 'system-health',
        label: 'System Health',
        path: '/system-health',
        icon: <Speed sx={{ fontSize: 18 }} />,
        roles: ROLES.ICT,
      },
      {
        id: 'audit-logs',
        label: 'Audit Logs',
        path: '/audit-logs',
        icon: <VerifiedUser sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
      {
        id: 'org-settings',
        label: 'Organisation Settings',
        path: '/org-settings',
        icon: <Settings sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
      {
        id: 'email-settings',
        label: 'Email Settings',
        path: '/smtp',
        icon: <Email sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
      {
        id: 'email-preview',
        label: 'Email Templates',
        path: '/email-preview',
        icon: <Visibility sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'banner-management',
        label: 'Banner Management',
        path: '/banner-management',
        icon: <Campaign sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'app-version-management',
        label: 'App Version Management',
        path: '/app-version-management',
        icon: <PhoneAndroid sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      {
        id: 'mobile-app-report',
        label: 'Mobile App Report',
        path: '/mobile-app-report',
        icon: <PhoneAndroid sx={{ fontSize: 18 }} />,
        roles: ['admin', 'ict_manager'],
      },
      // ── Help & tools (moved under System so they sit beneath the SYSTEM header) ──
      {
        id: 'help-center',
        label: 'Help Center',
        path: '/help',
        icon: <HelpOutline sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'release-notes',
        label: 'Release Notes',
        path: '/release-notes',
        icon: <HelpCenter sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'download-app',
        label: 'Download Mobile App',
        path: '/download-app',
        icon: <Devices sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'download-agent',
        label: 'Download Remote Agent',
        path: '/download-agent',
        icon: <Computer sx={{ fontSize: 18 }} />,
        roles: ROLES.ALL,
      },
      {
        id: 'deploy-wallpaper',
        label: 'Deploy Wallpaper',
        path: '/deploy-wallpaper',
        icon: <Palette sx={{ fontSize: 18 }} />,
        roles: ROLES.USER_MGT,
      },
      {
        id: 'wallpaper-schedules',
        label: 'Wallpaper Schedules',
        path: '/wallpaper-schedules',
        icon: <Schedule sx={{ fontSize: 18 }} />,
        roles: ROLES.USER_MGT,
      },
      {
        id: 'agent-config-password',
        label: 'Agent Config Password',
        path: '/agent-config-password',
        icon: <VpnKey sx={{ fontSize: 18 }} />,
        roles: ['admin'],
      },
    ],
  },
];

/**
 * BREADCRUMB_LABELS — maps URL path segments to human-readable names.
 * Consumed by the AppBreadcrumbs component.
 */
export const BREADCRUMB_LABELS = {
  home:              'Dashboard',
  dashboard:         'Dashboard',
  tickets:           'Tickets',
  'ticket-pool':     'Ticket Pool',
  'server-requests': 'Server Requests',
  'server-reports':  'Server Reports',
  'vpn-requests':    'VPN Requests',
  'vpn-reports':     'VPN Reports',
  'service-renewals': 'Reminders',
  'contract-analysis': 'Contract Analysis',
  assets:            'Asset Inventory',
  'management-dashboard': 'Asset Dashboard',
  maintenance:       'Maintenance',
  'maintenance-schedule': 'Maintenance Schedule',
  'maintenance-mgmt': 'Maintenance Management',
  backups:           'Backups',
  vendors:           'Vendors',
  users:             'User Management',
  departments:       'Departments',
  roles:             'Roles & Permissions',
  invite:            'Invite Users',
  'team-dashboard':  'Employee Lifecycle',
  reports:           'Reports & Analytics',
  'audit-logs':      'Audit Logs',
  smtp:              'Email Settings',
  notifications:     'Notifications',
  profile:           'Profile',
  help:              'Help Center',
  'release-notes':   'Release Notes',
  'download-app':    'Download Mobile App',
  'download-agent':  'Download Remote Agent',
  'deploy-wallpaper': 'Deploy Wallpaper',
  'wallpaper-schedules': 'Wallpaper Schedules',
  'agent-config-password': 'Agent Config Password',
  'system-health':   'System Health',
  'my-report':       'My Performance',
  'engineer-performance': 'Engineer Analytics',
  'my-tickets':      'My Overview',
  'my-dashboard':    'My Dashboard',

  // Problem Management
  'problem-dashboard': 'Dashboard',
  problems:            'Problems',
  'known-errors':      'Known Error Database',

  // Knowledge Management
  knowledge:    'Knowledge Base',
  articles:     'Articles',
  approval:     'Pending Approval',
  categories:   'Categories',
  new:          'New',
  edit:         'Edit',
  repository:   'Document Repository',
  documents:    'Document Library',

  // CMDB
  cmdb:             'CMDB',
  list:             'Configuration Items',
  services:         'Business Services',
  relationships:    'Relationships',
  'impact-analysis':'Impact Analysis',
  topology:         'Topology',
  dependencies:     'Dependencies',
  governance:       'Governance',
  sync:             'Synchronization',
  operations:       'Operations Center',
  phase2:           'Dashboard',

  // Asset Management
  hardware:         'Hardware',
  catalog:          'Catalog',
  requests:         'Requests',
  fulfilment:       'Fulfilment Queue',
  software:         'Software',
  licenses:         'Licenses',
  sam:              'SAM & Compliance',
  assignments:      'Assignments',
  'sam-reports':    'SAM Reports',
  repairs:          'Repair Center',
  'allocation-center': 'Allocation Center',
  'employee-portfolio': 'Employee Portfolio',
  'loan-center': 'Loan Center',
  'transfer-center': 'Transfer Center',
  'return-center': 'Return Center',
  'damage-recovery': 'Damage & Recovery',
  'reservation-center': 'Reservation Center',
  'inventory-intelligence': 'Inventory Intelligence',
  'bulk-operations': 'Bulk Operations',
  'qr-scanner': 'QR Scanner',
  'configuration-dashboard': 'Configuration Dashboard',
  'components': 'Component Registry',
  'configuration-baselines': 'Configuration Baselines',

  // Asset Intelligence & Financial (Sprint 5)
  'asset-intelligence': 'Asset Intelligence',
  financial:            'Financial Management',
  budget:               'Budget Forecast',
  replacement:          'Replacement Planner',

  // Warehouse & Logistics
  warehouse:        'Warehouse',
  stock:            'Stock Explorer',
  'goods-receipt':  'Goods Receipt',

  // Financial Management
  finance:          'Financial Management',
  budgets:          'Budgets',

  // Service Catalog
  'service-portal': 'Service Catalog',

  // Vendor Management
  contracts:        'Contracts',

  // Locations
  locations:        'Enterprise Locations',
  tree:             'Location Tree',
  import:           'Import',
  settings:         'Settings',
};

/**
 * Section ancestor labels for breadcrumb context.
 * Maps the first path segment to a section name.
 */
export const SECTION_ANCESTORS = {
  tickets:           { label: 'Service Desk', path: null },
  'my-tasks':        { label: 'Service Desk', path: null },
  'server-requests': { label: 'Service Desk', path: null },
  'server-reports':  { label: 'Service Desk', path: null },
  'vpn-requests':    { label: 'Service Desk', path: null },
  'vpn-reports':     { label: 'Service Desk', path: null },
  'software-requests': { label: 'Service Desk', path: null },
  'service-renewals': { label: 'Service Desk', path: null },
  'contract-analysis': { label: 'Service Desk', path: null },
  'ticket-pool':     { label: 'Service Desk', path: null },
  problems:          { label: 'Problem & Change', path: null },
  'problem-dashboard':{ label: 'Problem & Change', path: null },
  'known-errors':    { label: 'Problem & Change', path: null },
  knowledge:         { label: 'Knowledge & Services', path: null },
  repository:        { label: 'Knowledge & Services', path: null },
  hardware:          { label: 'Assets', path: null },
  software:          { label: 'Assets', path: null },
  assets:            { label: 'Assets', path: null },
  repairs:           { label: 'Assets', path: null },
  'allocation-center': { label: 'Assets', path: null },
  'employee-portfolio': { label: 'Assets', path: null },
  'loan-center': { label: 'Assets', path: null },
  'transfer-center': { label: 'Assets', path: null },
  'return-center': { label: 'Assets', path: null },
  'damage-recovery': { label: 'Assets', path: null },
  'reservation-center': { label: 'Assets', path: null },
  'inventory-intelligence': { label: 'Assets', path: null },
  'bulk-operations': { label: 'Assets', path: null },
  'qr-scanner': { label: 'Assets', path: null },
  'configuration-dashboard': { label: 'Assets', path: null },
  'components': { label: 'Assets', path: null },
  'configuration-baselines': { label: 'Assets', path: null },
  'asset-intelligence': { label: 'Assets', path: null },
  maintenance:       { label: 'Problem & Change', path: null },
  'maintenance-schedule': { label: 'Problem & Change', path: null },
  'maintenance-mgmt': { label: 'Problem & Change', path: null },
  backups:           { label: 'Problem & Change', path: null },
  vendors:           { label: 'Business Operations', path: null },
  cmdb:              { label: 'CMDB', path: null },
  warehouse:         { label: 'Business Operations', path: null },
  finance:           { label: 'Business Operations', path: null },
  'service-portal':  { label: 'Knowledge & Services', path: null },
  projects:          { label: 'Business Operations', path: null },
  users:             { label: 'People & Access', path: null },
  departments:       { label: 'People & Access', path: null },
  roles:             { label: 'People & Access', path: null },
  invite:            { label: 'People & Access', path: null },
  'team-dashboard':  { label: 'People & Access', path: null },
  locations:         { label: 'Configuration', path: null },
  reports:           { label: 'Reports', path: null },
  'audit-logs':      { label: 'System', path: null },
  'system-health':   { label: 'System', path: null },
  smtp:              { label: 'System', path: null },
  'email-preview': { label: 'System', path: null },
  'banner-management': { label: 'System', path: null },
  'app-version-management': { label: 'System', path: null },
  'mobile-app-report': { label: 'System', path: null },
  'org-settings':    { label: 'System', path: null },
  'theme-settings':  { label: 'Configuration', path: null },
  'ticket-categories': { label: 'Configuration', path: null },
  'vendor-service-types': { label: 'Configuration', path: null },
  'asset-categories': { label: 'Configuration', path: null },
  'oem-settings':    { label: 'Configuration', path: null },
  'sla-settings':    { label: 'Configuration', path: null },
};

/**
 * Returns nav groups filtered for a given user role.
 * Groups with no accessible children are excluded entirely.
 */
export function getNavForRole(user) {
  // Backward compatible: accepts either a full user object or a bare role string.
  const userObj = user && typeof user === 'object' ? user : { role: user };
  const role = userObj.role;
  return NAV_GROUPS.map(group => ({
    ...group,
    children: group.children.filter(item => {
      if (item.hidden) return false;
      // Department-based gate (e.g. HR on-behalf returns) takes precedence.
      if (item.deptGate) return item.deptGate(userObj);
      return !item.roles || item.roles.includes(role);
    }),
  })).filter(group => group.children.length > 0);
}

/**
 * resolveBreadcrumbTrail — derive a breadcrumb trail from the navigation tree so
 * breadcrumbs always mirror the sidebar grouping.
 *
 * Walks NAV_GROUPS and finds the nav item whose `path` best matches the current
 * URL (exact match preferred, otherwise the longest path that is a prefix of the
 * URL — so detail routes like /cmdb/abc123 still resolve to the CMDB list item).
 *
 * Returns an array of { label, path } from the top-level group down to the
 * matched item (and its sub-item, if any), e.g.:
 *   /hardware/procurement       → [{Hardware}, {Procurement}]
 *   /maintenance-mgmt/list      → [{Problem & Change?}, {Maintenance Management}, {All Maintenance}]
 *   /locations                  → [{Reports? no — group it lives in}]
 * The leading "Dashboard" crumb and current-label override are handled by the
 * breadcrumb component. Returns null when nothing in the nav tree matches.
 */
export function resolveBreadcrumbTrail(pathname) {
  if (!pathname) return null;
  const url = pathname.replace(/\/+$/, '') || '/';

  let best = null; // { len, trail: [{label, path}] }

  const consider = (candidatePath, trail) => {
    if (!candidatePath) return;
    const p = candidatePath.replace(/\/+$/, '') || '/';
    const isMatch = url === p || url.startsWith(p + '/');
    if (!isMatch) return;
    if (!best || p.length > best.len) {
      best = { len: p.length, trail };
    }
  };

  for (const group of NAV_GROUPS) {
    const groupCrumb = { label: group.label, path: null };
    for (const item of group.children || []) {
      // The item itself
      if (item.path) {
        consider(item.path, [groupCrumb, { label: item.label, path: item.path }]);
      }
      // Nested sub-items (e.g. maintenance-mgmt/list)
      for (const sub of item.children || []) {
        if (sub.path) {
          consider(sub.path, [
            groupCrumb,
            { label: item.label, path: item.path || null },
            { label: sub.label, path: sub.path },
          ]);
        }
      }
    }
  }

  if (!best) return null;

  // Drop a trailing sub-item that resolves to the exact same path as its parent
  // item (e.g. the maintenance "Dashboard" sub-item shares /maintenance-mgmt).
  const trail = best.trail.filter((c, i, arr) => {
    const prev = arr[i - 1];
    const isRedundantTail = i === arr.length - 1 && prev && prev.path && c.path === prev.path;
    return !isRedundantTail;
  });

  return trail;
}
